import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/lib/api/endpoints";
import type {
  DispatchBoardResponse,
  DispatchSettingsView,
} from "@/lib/api/dispatchTypes";
import { hasPermission, PERMISSIONS, useSession } from "@/modules/auth/useSession";
import { useApiQuery } from "@/shared/queries";
import {
  dispatchByInvoice,
  nextOfferDeadline,
  serverSkewMs,
  type BillDispatch,
} from "./dispatchBoard";

/**
 * The dispatch query keys. Under `["restaurant", "dispatch", …]`, so the
 * `floor` revision family (`shared/realtime.ts`, plan D-19) refreshes them:
 * the backend's `realtime_dispatch_changed()` trigger bumps `floor` on every
 * assignment, problem, shift and handover write.
 */
export const dispatchQueryKeys = {
  all: ["restaurant", "dispatch"] as const,
  board: ["restaurant", "dispatch", "board"] as const,
  settings: ["restaurant", "dispatch", "settings"] as const,
};

/** Who may read the board (T1): dispatch managers and driver-cash confirmers. */
export function useDispatchAccess() {
  const { data: session } = useSession();
  const canManage = hasPermission(session, PERMISSIONS.DISPATCH_MANAGE);
  const canConfirmCash = hasPermission(session, PERMISSIONS.DISPATCH_CASH_CONFIRM);
  const canEditSettings = hasPermission(session, PERMISSIONS.SETTINGS_MANAGE);
  return {
    canManage,
    canSeeBoard: canManage || canConfirmCash,
    canReadSettings: canManage || canEditSettings,
    canEditSettings,
  };
}

/**
 * The till's dispatch board (T1).
 *
 * Refreshed by the realtime `floor` signal and on focus like every floor read,
 * never on a timer. The one exception is data-driven, not periodic: when an
 * offer's deadline passes, the board is read ONCE more so the tile turns from
 * the countdown to "did not answer" from the server's own word (lazy expiry,
 * D-11), even on a till nobody touches.
 *
 * `enabled` lets the floor skip the request while it has no delivery bag.
 */
export function useDispatchBoard({ enabled }: { enabled: boolean }) {
  const access = useDispatchAccess();
  const queryClient = useQueryClient();
  const on = enabled && access.canSeeBoard;
  const query = useApiQuery<DispatchBoardResponse>(
    dispatchQueryKeys.board,
    endpoints.restaurant.dispatchBoard,
    { enabled: on, refetchOnWindowFocus: true },
  );
  const board = on ? query.data : undefined;

  // The server's clock against ours, measured when the answer arrived.
  const dataUpdatedAt = query.dataUpdatedAt;
  const skewMs = useMemo(
    () => (board && dataUpdatedAt ? serverSkewMs(board.serverTime, dataUpdatedAt) : 0),
    [board, dataUpdatedAt],
  );

  // One re-read at the earliest offer's deadline — once per offer (G13 M1):
  // an offer the server still answers OFFERED past its deadline is not
  // re-read again, so a clock skew or a skipped lock cannot spin the board.
  const rereadFor = useRef(new Set<string>());
  useEffect(() => {
    if (board) {
      // Forget offers that left the board (accepted, ended): the set stays small.
      const offered = new Set(
        board.assignments.filter((a) => a.status === "OFFERED").map((a) => a.id),
      );
      for (const id of rereadFor.current) if (!offered.has(id)) rereadFor.current.delete(id);
    }
    const next = nextOfferDeadline(board, Date.now(), skewMs, rereadFor.current);
    if (next === null) return;
    const timer = window.setTimeout(() => {
      for (const id of next.assignmentIds) rereadFor.current.add(id);
      void queryClient.invalidateQueries({ queryKey: dispatchQueryKeys.board });
    }, next.waitMs);
    return () => window.clearTimeout(timer);
  }, [board, queryClient, skewMs]);

  const byInvoice = useMemo(() => dispatchByInvoice(board), [board]);
  const hasOffer = Boolean(board?.assignments.some((a) => a.status === "OFFERED"));
  const now = useSecondClock(hasOffer);

  return {
    board,
    byInvoice,
    skewMs,
    now,
    isLoading: on && query.isLoading,
    error: on ? query.error : null,
    lookup: (invoiceId: string): BillDispatch | undefined => byInvoice.get(invoiceId),
  };
}

/**
 * A once-a-second clock for the offer countdown, running only while an offer
 * is on the board. It re-renders; it never fetches.
 */
function useSecondClock(running: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    const tick = () => setNow(Date.now());
    // The first tick at once (an offer that just appeared must not count
    // from a stale "now"), then once a second.
    const first = window.setTimeout(tick, 0);
    const timer = window.setInterval(tick, 1000);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(timer);
    };
  }, [running]);
  return now;
}

/** The accept timeout (T8 read): dispatch managers and settings managers. */
export function useDispatchSettings() {
  const access = useDispatchAccess();
  return useApiQuery<DispatchSettingsView>(
    dispatchQueryKeys.settings,
    endpoints.restaurant.dispatchSettings,
    { enabled: access.canReadSettings },
  );
}
